import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { JupiterTheme } from '../../theme/types';
import {
  BasePaginationProps,
  QueryResultsChange,
  PaginationSortingProps,
} from '../../types';
import NoQueryResults from './NoQueryResults';
import QueryToolbar from './QueryToolbar';
import ResultForFormatContainer from './ResultForFormatContainer';
import useQueryResultLogic, { HookProps } from './useQueryResultLogic';
import { J1QLResultData } from '../../../types/J1QL';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    resultPaper: {
      height: '100%',
      maxHeight: '100%',
      minHeight: '100%',
      width: 'auto',
      position: 'relative',
      flexFlow: 'wrap',
      background: theme.palette.grey[50],
      boxShadow: 'none',
      margin: 0,
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
    },
    hideToolbar: {
      gridTemplateRows: '1fr',
    },
  })
);

interface Props extends Omit<HookProps, 'result'> {
  result?: J1QLResultData;
  loading?: boolean;
  savable?: boolean;
  className?: string;
  classes?: Record<'graphView', string>;
  toolbarIcons?: {
    title: string;
    icon: JSX.Element;
    onClick: (event: any) => void;
  }[];
  hideToolbar?: boolean;
  disableRowClick?: boolean;
  useRowDataForEntityDrawer?: boolean;
  noDataEdit?: boolean;
  onResultsChange?: (changes: QueryResultsChange[]) => void;
  onRemoveResult?: VoidFunction;
  refetch?: () => Promise<void>;
  loadMoreOrderBy?: PaginationSortingProps['loadMoreOrderBy'];
  loadMore?: BasePaginationProps['loadMore'];
  totalResultCount?: BasePaginationProps['totalResultCount'];
  hasMinHeight?: boolean;
  parentSelectedRowID?: string | null;
  parentSetSelectedRowID?: (id: string | null) => void;
  astronaut?: boolean;
}

export const QueryResult = ({
  className,
  classes: propClasses,
  queryInput,
  loading = false,
  result = { type: 'list', data: [] },
  hasMinHeight = true,
  onResultsChange,
  onRemoveResult,
  savable,
  toolbarIcons,
  loadMore,
  loadMoreOrderBy,
  refetch,
  hideToolbar,
  disableRowClick,
  useRowDataForEntityDrawer,
  noDataEdit,
  totalResultCount,
  parentSelectedRowID,
  parentSetSelectedRowID,
  astronaut,
}: Props) => {
  const classes = useStyles({ hasMinHeight });
  const {
    query,
    selectedFormat,
    setSelectedFormat,
    handleSelectedDownload,
    exporting,
    hasResults,
    hasNextPage,
  } = useQueryResultLogic({
    queryInput,
    result,
  });

  return (
    <Paper
      data-testid="QueryResult"
      className={clsx(
        classes.resultPaper,
        hideToolbar && classes.hideToolbar,
        className
      )}
    >
      {!hideToolbar && (
        <QueryToolbar
          query={query}
          onRemoveResult={onRemoveResult}
          savable={savable}
          selectedFormat={selectedFormat}
          onSelectedFormat={setSelectedFormat}
          onSelectedDownload={handleSelectedDownload}
          isExporting={exporting.state}
          toolbarIcons={toolbarIcons}
          addToInsights
          exportable
          hasResults={hasResults}
        />
      )}
      {!loading && !hasResults ? (
        <NoQueryResults
          resultsAre={(queryInput as any)?.resultsAre}
          astronaut={astronaut}
        />
      ) : (
        <ResultForFormatContainer
          classes={propClasses}
          disableRowClick={disableRowClick}
          result={result}
          onResultsChange={onResultsChange}
          queryInput={{ query, ...queryInput }}
          selectedFormat={selectedFormat}
          paginationProps={
            // Some uses of QueryResult do not want pagination supported
            // namely, some insights functionality, so we need to support
            // passing undefined paginationProps to support that backwards
            // compatibility
            loadMore
              ? {
                  loadMoreOrderBy,
                  loadMore,
                  totalResultCount,
                  hasNextPage,
                  loading,
                }
              : undefined
          }
          refetch={refetch}
          useRowDataForEntityDrawer={useRowDataForEntityDrawer}
          noDataEdit={noDataEdit}
          parentSelectedRowID={parentSelectedRowID}
          parentSetSelectedRowID={parentSetSelectedRowID}
        />
      )}
    </Paper>
  );
};

export default QueryResult;
